import { useEffect } from 'react';

import type { ApiListingApplicationId } from '#/api/types/ApiTypedId';
import { Box } from '#/components/Box/Box';
import { VolopDetailsList } from '#/components/DetailsList/Volop/VolopDetailsList';
import { PageToolbar } from '#/components/PageToolbar/PageToolbar';
import { PrintHidden } from '#/components/PrintHidden/PrintHidden';
import { useUser } from '#/contexts/UserEnvironmentContext';
import { useKeyedState } from '#/hooks/useKeyedState';
import { useI18n } from '#/i18n/hooks/useI18n';
import { trackViewListing } from '#/modules/listing/analytics/trackViewListing';
import { ListingViewAdminTools } from '#/modules/listing/components/ListingView/AdminTools/ListingViewAdminTools';
import { ListingViewAppliedMessage } from '#/modules/listing/components/ListingView/AppliedMessage/ListingViewAppliedMessage';
import { ListingViewApplyAts } from '#/modules/listing/components/ListingView/Apply/ListingViewApplyAts';
import { ListingViewApplyExternal } from '#/modules/listing/components/ListingView/Apply/ListingViewApplyExternal';
import { ListingViewIdealistAtsUserApplicationForm } from '#/modules/listing/components/ListingView/AtsApplicationForm/ListingViewIdealistAtsUserApplicationForm';
import { ListingViewBody } from '#/modules/listing/components/ListingView/Body/ListingViewBody';
import { ListingViewFooter } from '#/modules/listing/components/ListingView/Footer/ListingViewFooter';
import { ListingViewHeader } from '#/modules/listing/components/ListingView/Header/ListingViewHeader';
import { ListingViewHeaderSearchContext } from '#/modules/listing/components/ListingView/HeaderSearchContext/ListingViewHeaderSearchContext';
import { ListingViewMain } from '#/modules/listing/components/ListingView/Main/ListingViewMain';
import { ListingViewSection } from '#/modules/listing/components/ListingView/Section/ListingViewSection';
import { ListingViewSidebar } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebar';
import { ListingViewSidebarContent } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebarContent';
import { ListingViewSidebarImage } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebarImage';
import { ListingViewSidebarLinks } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebarLinks';
import { ListingViewSidebarTitle } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebarTitle';
import { ListingViewSignupCta } from '#/modules/listing/components/ListingView/SignupCta/ListingViewSignupCta';
import { ListingViewDescriptionSection } from '#/modules/listing/components/ListingView/sections/Description/ListingViewDescriptionSection';
import { ListingViewApplySection } from '#/modules/listing/components/ListingView/sections/ListingViewApplySection';
import { ListingViewLocationSection } from '#/modules/listing/components/ListingView/sections/ListingViewLocationSection';
import { trackViewListingAtsSection } from '#/modules/listing/helpers/trackViewListingAtsSection';
import { useListingApplicationSubmission } from '#/modules/listing/hooks/listingApplicationSubmission/useListingApplicationSubmission';
import { useListingApplicationTemporaryStorageSubmission } from '#/modules/listing/hooks/listingApplicationSubmission/useListingApplicationTemporaryStorageSubmission';
import { useSaveListingAndOrg } from '#/modules/listing/hooks/useSaveListingAndOrg';
import { ListingViewStatus } from '#/modules/listing/types/ListingViewStatus';
import type { ApiVolop } from '#/modules/listing/volops/api/types/ApiVolop';
import { ViewVolopActionButtons } from '#/modules/listing/volops/components/ActionButtons/ViewVolopActionButtons';
import { getVolopApplySectionTitle } from '#/modules/listing/volops/helpers/getVolopApplySectionTitle';
import { SearchContextHeader } from '#/modules/search/components/Context/SearchContextHeader';
import { useSearchContextHeader } from '#/modules/search/containers/SearchContextHeader/useSearchContextHeader';
import { trackClicked } from '#/utils/analytics/track/trackClicked';
import { isUserOrgOwner } from '#/utils/listings/getOrgOwner';
import { isExternalURL } from '#/utils/url/isExternalURL';
import { showModal } from '#/zustand-stores/modalStore';

type Props = {
  volop: ApiVolop;
  listingApplicationId: ApiListingApplicationId | null;
  initiallySaved: boolean;
  orgInitiallySaved: boolean;
};

export function ViewVolopPage({
  volop,
  listingApplicationId,
  initiallySaved,
  orgInitiallySaved,
}: Props) {
  const { i18n, locale } = useI18n();

  const {
    address,
    applyText,
    applyTextTruncated,
    applyUrl,
    applyEmail,
    areasOfFocus,
    id,
    hasAts,
    startDate,
    directions,
    endDate,
    startsLocal,
    endsLocal,
    functions,
    requestedAttachments,
    requestedAttachmentsHash,
    firstPublished,
    driversLicenseRequired,
    backgroundCheckRequired,
    orientationRequired,
    feeRequired,
    feeAmount,
    isPostedAnonymously,
    isRecurring,
    name,
    timezone,
    welcomeFamilies,
    welcomeTeens,
    welcomeGroups,
    welcomeAge55Plus,
    welcomeIntl,
    welcomePrivateCorpGroups,
    detailsStipendProvided,
    detailsCulturalSupport,
    detailsHousingAvailable,
    detailsTrainingProvided,
    detailsAcademicCreditAvailable,
    detailsWheelchairAccessible,
    timesOfDay,
    type,
    image,
    description,
    descriptionTruncated,
    expectedTime,
    remoteOptions,
    url,
    org,
    ageRequirement,
    otherRequirements,
    numVolunteersNeeded,
    commitmentDetails,
    canBeDoneInADay,
  } = volop;

  const [applied, setApplied] = useKeyedState(Boolean(listingApplicationId), {
    key: volop.id,
  });

  const { searchContext } = useSearchContextHeader({ currentObjectId: id });

  const { user } = useUser();
  const isOrgOwner = isUserOrgOwner(user, org?.id);

  useEffect(() => {
    trackViewListing({
      status: 'published',
      hasAts,
      listingType: type,
      listingId: id,
      listingName: name,
      orgId: org?.id,
      orgName: org?.name,
      orgType: org?.orgType,
    });
  }, [hasAts, id, name, org?.id, org?.name, org?.orgType, type]);

  const orgId = org?.id ?? null;
  const orgLogo = org?.logo ?? null;
  const orgName = org?.name ?? null;
  const orgType = org?.orgType ?? null;
  const orgUrl = org?.url[locale] ?? null;

  const {
    save,
    saved,
    unsave,
    orgSaved,
    saveOrg,
    unsaveOrg,
    shouldShowSaveOrgTooltip,
    dismissSaveOrgTooltip,
  } = useSaveListingAndOrg({
    listing: volop,
    initiallySaved,
    orgInitiallySaved,
    searchSavedListingsStore: null,
  });

  const { listingApplicationInitialValues, submitListingApplication } =
    useListingApplicationSubmission({
      listingId: id,
      listingType: type,
      orgId,
      orgType,
      applied,
      onApplied: () => setApplied(true),
    });

  const { formRef, temporaryStorageInitialValues } =
    useListingApplicationTemporaryStorageSubmission();

  return (
    <>
      {isOrgOwner && org && (
        <PageToolbar
          centered
          message={i18n('Your organization posted this volunteer opportunity')}
          tools={
            <ListingViewAdminTools
              listingId={id}
              listingType="VOLOP"
              orgId={org.id}
              canPost={Boolean(
                user?.orgs.find((o) => o.id === org.id)?.canPostVolops,
              )}
              hasAts={hasAts}
            />
          }
        />
      )}

      <ListingViewBody>
        <ListingViewSidebar maxWidth={288}>
          <ListingViewSidebarImage
            orgLogo={orgLogo}
            orgName={orgName}
            orgURLForCurrentLocale={orgUrl}
          />
          <ListingViewSidebarTitle
            address={address}
            listingName={name}
            listingType="VOLOP"
            orgName={orgName}
            orgUrl={orgUrl}
            remoteOptions={remoteOptions}
          />
          <ListingViewSidebarContent
            firstPublished={firstPublished}
            applied={applied}
            listingType={type}
            showScrollToApplyButton={hasAts}
            listingViewStatus={ListingViewStatus.PUBLISHED}
          />
          <ListingViewSidebarLinks
            searchContext={searchContext}
            orgUrl={orgUrl}
          />
        </ListingViewSidebar>

        <ListingViewMain>
          {searchContext && (
            <ListingViewHeaderSearchContext searchContext={searchContext} />
          )}

          <ListingViewHeader
            address={address}
            isPostedAnonymously={isPostedAnonymously}
            listingId={id}
            listingName={name}
            listingType={type}
            listingFirstPublished={firstPublished}
            orgName={orgName}
            orgType={orgType}
            orgUrl={orgUrl}
            remoteOptions={remoteOptions}
            canBeDoneInADay={canBeDoneInADay}
            listingViewStatus={ListingViewStatus.PUBLISHED}
            actionButtons={
              <ViewVolopActionButtons
                volop={volop}
                applied={applied}
                volopSaved={saved}
                onSaveVolop={save}
                onUnsaveVolop={unsave}
                orgSaved={orgSaved}
                onSaveOrg={saveOrg}
                onUnsaveOrg={unsaveOrg}
                shouldShowSaveOrgTooltip={shouldShowSaveOrgTooltip}
                onDismissSaveOrgTooltip={dismissSaveOrgTooltip}
              />
            }
          />

          <ListingViewSection title={i18n('Details')} noBorderTop>
            <VolopDetailsList
              areasOfFocus={areasOfFocus}
              startDate={startDate}
              endDate={endDate}
              startsLocal={startsLocal}
              endsLocal={endsLocal}
              driversLicenseRequired={driversLicenseRequired}
              backgroundCheckRequired={backgroundCheckRequired}
              orientationRequired={orientationRequired}
              feeRequired={feeRequired}
              feeAmount={feeAmount}
              isRecurring={isRecurring}
              timezone={timezone}
              welcomeFamilies={welcomeFamilies}
              welcomeTeens={welcomeTeens}
              welcomeGroups={welcomeGroups}
              welcomeAge55Plus={welcomeAge55Plus}
              welcomeIntl={welcomeIntl}
              welcomePrivateCorpGroups={welcomePrivateCorpGroups}
              detailsStipendProvided={detailsStipendProvided}
              detailsTrainingProvided={detailsTrainingProvided}
              detailsHousingAvailable={detailsHousingAvailable}
              detailsCulturalSupport={detailsCulturalSupport}
              detailsAcademicCreditAvailable={detailsAcademicCreditAvailable}
              detailsWheelchairAccessible={detailsWheelchairAccessible}
              timesOfDay={timesOfDay}
              expectedTime={expectedTime}
              ageRequirement={ageRequirement}
              otherRequirements={otherRequirements}
              numVolunteersNeeded={numVolunteersNeeded}
              commitmentDetails={commitmentDetails}
            />
          </ListingViewSection>

          <ListingViewDescriptionSection
            listingName={name}
            image={image}
            description={description}
            descriptionTruncated={descriptionTruncated}
            handleShowEmailModal={(link) => {
              if (!user && isExternalURL(link.href)) {
                showModal('EMAIL_ALERT', {
                  listingFunctions: functions,
                  orgId: org?.id,
                  listingName: name,
                  listingType: 'VOLOP',
                });
              }
            }}
            showMoreLinkTitle={i18n(
              'Read more about this volunteer opportunity',
            )}
            onToggleDetails={(isOpen) => {
              if (!isOpen) return;

              trackClicked('read more about this listing', {
                listing_type: volop.type,
                locale: volop.locale,
              });
            }}
          />

          <ListingViewLocationSection
            listingType={type}
            address={address}
            directions={directions}
            remoteOptions={remoteOptions}
          />

          <ListingViewApplySection
            title={getVolopApplySectionTitle({ i18n, hasAts })}
          >
            {hasAts ? (
              // oxlint-disable-next-line react/jsx-no-useless-fragment
              <>
                {applied ? (
                  <ListingViewAppliedMessage />
                ) : (
                  <ListingViewApplyAts
                    applyText={applyText}
                    applicationForm={
                      <ListingViewIdealistAtsUserApplicationForm
                        listingId={id}
                        listingType="VOLOP"
                        requestedAttachments={requestedAttachments}
                        submission={{
                          onSubmit: submitListingApplication,
                          listingApplicationInitialValues,
                          requestedAttachmentsHash,
                          temporaryStorage: {
                            formRef,
                            initialValues: temporaryStorageInitialValues,
                          },
                        }}
                      />
                    }
                    onReach={() =>
                      trackViewListingAtsSection({
                        listingId: id,
                        listingType: 'VOLOP',
                        orgId,
                        orgType,
                        applied,
                      })
                    }
                  />
                )}
              </>
            ) : (
              <ListingViewApplyExternal
                applyEmail={applyEmail}
                applyText={applyText}
                applyTextTruncated={applyTextTruncated}
                applyUrl={applyUrl}
                applied={applied}
                onMarkAsApplied={() => Promise.resolve()}
                functions={functions}
                listingName={name}
                listingType="VOLOP"
                listingId={id}
                orgId={orgId}
                orgType={orgType}
                synonym={null}
                isPreview={false}
              />
            )}
          </ListingViewApplySection>

          {searchContext && (
            <PrintHidden>
              <Box mt={70} mb={48}>
                <SearchContextHeader searchContext={searchContext} noDivider />
              </Box>
            </PrintHidden>
          )}
        </ListingViewMain>
      </ListingViewBody>

      {!user && (
        <PrintHidden>
          <ListingViewFooter>
            <ListingViewSignupCta listingUrl={url[locale]} type={type} />
          </ListingViewFooter>
        </PrintHidden>
      )}
    </>
  );
}
